import {
  assertSafeCalendarExternalUrl,
  readBoundedCalendarIcsResponse,
} from '@streamlain/calendar-ics-security';
import { addDays } from 'date-fns/addDays';

import {
  parseCalendarAttendees,
  parseCalendarIcs,
  renderCalendarIcs,
} from '../../calendar-domain.js';
import {
  calendarFeedTokenUrl,
  calendarReconcileUrl,
} from '../../core/calendar-reconcile-contract.js';
import type { CalendarIcsSubscription } from '../../define.js';
import type {
  CalendarEvent,
  CalendarReconcileColumns,
  CalendarReconcileContext,
  CalendarReconcileSnapshot,
} from '../calendar-view-types.js';
import { randomRequestId } from '../calendar-view-utils.js';

type FetchLike = typeof fetch;

export function createCalendarIcsSubscriptionFromUrl(
  url: string,
  id = randomRequestId()
): CalendarIcsSubscription {
  const trimmed = url.trim();
  if (!trimmed) {
    throw new Error('Feed URL is required');
  }
  const safeUrl = assertSafeCalendarExternalUrl(trimmed);
  const parsed = new URL(safeUrl);
  return {
    enabled: true,
    id,
    name: parsed.hostname,
    url: safeUrl,
  };
}

export async function fetchIcsSubscriptionEvents(
  subscription: CalendarIcsSubscription,
  fetcher: FetchLike = fetch,
  signal?: AbortSignal
): Promise<CalendarEvent[]> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetcher(
      assertSafeCalendarExternalUrl(subscription.url),
      {
        credentials: 'omit',
        method: 'GET',
        signal: signal
          ? AbortSignal.any([signal, controller.signal])
          : controller.signal,
      }
    );
    if (!response.ok) {
      throw new Error(`Feed returned ${response.status}`);
    }
    return parseIcsSubscriptionEvents(
      subscription,
      await readBoundedCalendarIcsResponse(response)
    );
  } finally {
    clearTimeout(timeout);
  }
}

export function parseIcsSubscriptionEvents(
  subscription: CalendarIcsSubscription,
  text: string
): CalendarEvent[] {
  return parseCalendarIcs(text).map(
    (event, index): CalendarEvent => ({
      allDay: event.allDay,
      attendeeCount: parseCalendarAttendees(event.attendees).length,
      attendees: event.attendees,
      calendarId: subscription.name || event.calendarId || 'Subscribed',
      description: event.description,
      endAt: event.endAt,
      externalReadonly: true,
      locationPhysical: event.locationPhysical,
      locationVirtual: event.locationVirtual,
      recurrence: event.recurrence,
      recurrenceExdate: event.recurrenceExdate,
      recurrenceId: event.recurrenceId,
      reminder: '',
      rowId: `ics:${subscription.id}:${index}:${event.startAt.toISOString()}`,
      startAt: event.startAt,
      status: event.status,
      timezone: event.timezone,
      title: event.title,
      viewId: subscription.id,
      visibility: '',
    })
  );
}

export function renderCalendarEventsIcs(
  events: readonly CalendarEvent[]
): string {
  return renderCalendarIcs(events);
}

export function downloadCalendarIcs(content: string, filename: string): void {
  const blob = new Blob([content], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function createSignedCalendarFeedUrl({
  columns,
  context,
  fetcher = fetch,
  flushDocs,
  origin,
  viewId,
}: {
  columns: CalendarReconcileColumns;
  context: CalendarReconcileContext;
  fetcher?: FetchLike;
  flushDocs: (context: CalendarReconcileContext) => Promise<void>;
  origin: string;
  viewId: string;
}): Promise<string> {
  await flushDocs(context);
  const response = await fetcher(calendarFeedTokenUrl(context), {
    body: JSON.stringify({
      columns,
      ttlSeconds: 90 * 24 * 60 * 60,
      viewId,
    }),
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(
      `Calendar feed URL could not be created: ${response.status}`
    );
  }
  const payload = (await response.json()) as { url?: string };
  if (!payload.url) {
    throw new Error('Calendar feed URL response was empty');
  }
  return new URL(payload.url, origin).toString();
}

export async function submitCalendarReconcile({
  columns,
  context,
  fetcher = fetch,
  flushDocs,
  previousEvent,
  requestId = randomRequestId(),
  rowId,
  skipMail = false,
  viewId,
}: {
  columns: CalendarReconcileColumns;
  context: CalendarReconcileContext;
  fetcher?: FetchLike;
  flushDocs: (context: CalendarReconcileContext) => Promise<void>;
  previousEvent: CalendarReconcileSnapshot | null;
  requestId?: string;
  rowId: string;
  skipMail?: boolean;
  viewId: string;
}): Promise<void> {
  await flushDocs(context);
  const response = await fetcher(calendarReconcileUrl(context, rowId), {
    body: JSON.stringify({
      columns,
      previousEvent,
      reminderWindowEndAt: addDays(new Date(), 395).toISOString(),
      reminderWindowStartAt: new Date().toISOString(),
      requestId,
      skipMail,
      viewId,
    }),
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(await calendarHttpError(response, 'reconcile'));
  }
}

async function calendarHttpError(
  response: Response,
  operation: string
): Promise<string> {
  const status = response.status;
  const detail = await calendarHttpErrorDetail(response);
  return detail
    ? `Calendar ${operation} failed: ${status} (${detail})`
    : `Calendar ${operation} failed: ${status}`;
}

async function calendarHttpErrorDetail(response: Response): Promise<string> {
  try {
    const body = await response.text();
    if (!body) return '';
    try {
      const parsed = JSON.parse(body) as { message?: unknown };
      const message = parsed.message;
      if (Array.isArray(message)) return message.join('; ');
      if (typeof message === 'string') return message;
    } catch {
      // Fall back to the plain response body below.
    }
    return body.slice(0, 500);
  } catch {
    return '';
  }
}
